import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { toast } from "react-toastify"
import { useFormik } from "formik"
import * as Yup from "yup"

import { useTranslation } from "react-i18next"

import { InputField } from "components/inputs/InputField"
import { ModalBonus } from "components/modals/ModalBonus"
import { BonusOfferSelection } from "components/modals/ModalPromoCode/BonusOfferSelection"

import { useProps } from "contexts/PropsContext"

import { useIsMounted } from "hooks/useIsMounted"

import { BonusService } from "services/bonus/bonus.service"
import { promoCodeService } from "services/promoCode/promoCode.service"

import { BonusType } from "types/api"

import { isAuthorityUnavailableError } from "util/api"

import { TabHandle } from "./CustomBonusTab"

type SavedBonus = { id: string; title: string } | null

interface DepositBonusTabProps {
  userId: string
  open: boolean
  onSuccess: () => void
  onClose: () => void
}

export const DepositBonusTab = forwardRef<TabHandle, DepositBonusTabProps>(({ userId, open, onSuccess, onClose }, ref) => {
  const isMounted = useIsMounted()
  const { user } = useProps()
  const { t } = useTranslation("translation", { useSuspense: false })

  const [depositBonuses, setDepositBonuses] = useState<{ id: string; title: string }[]>([])
  const [isLoadingDepositBonuses, setIsLoadingDepositBonuses] = useState(false)
  const [openBonusModal, setOpenBonusModal] = useState(false)
  const [savedBonus, setSavedBonus] = useState<SavedBonus>(null)

  const closeBonusModalHandler = (id: string, title: string) => {
    setSavedBonus({ id, title })
    formik.setFieldValue("bonusId", id)
    setOpenBonusModal(false)
  }

  const formik = useFormik({
    initialValues: { bonusId: "", code: "" },
    enableReinitialize: false,
    validationSchema: Yup.object().shape({
      bonusId: Yup.string().required(t("addUserBonus.selectDepositBonus")),
      code: Yup.string().optional()
    }),
    onSubmit: async (values, helpers) => {
      try {
        await promoCodeService.assignDepositBonusOffer({
          userId,
          bonusId: values.bonusId,
          code: values.code || undefined
        })
        toast.success(String(t("addUserBonus.depositOfferSuccess")))
        onSuccess()
        onClose()
      } catch (err: any) {
        // SOFT-556: 503 → bonus-service unavailable, grant fail-closed; retryable message, not a
        // false success or generic failure. The "already has an active bonus" 400 keeps its message.
        if (isAuthorityUnavailableError(err)) {
          toast.error(String(t("addUserBonus.bonusServiceUnavailable")))
        } else {
          toast.error(err?.data?.message ?? err?.message ?? t("addUserBonus.depositOfferFailed"))
        }
        if (isMounted()) {
          helpers.setStatus({ success: false })
          helpers.setSubmitting(false)
        }
      }
    }
  })

  useImperativeHandle(ref, () => ({
    handleSubmit: () => formik.handleSubmit(),
    isSubmitting: formik.isSubmitting
  }))

  useEffect(() => {
    const loadDepositBonuses = async () => {
      try {
        setIsLoadingDepositBonuses(true)
        const res = await BonusService.getBonusesList({
          page: 1,
          pageSize: 100,
          filters: {
            type: BonusType.PROMO,
            active: true,
            tenant: user?.tenant_id
          },
          sortRow: "created_at",
          sortOrder: "desc"
        })

        const data = res?.data ?? []
        setDepositBonuses(
          data.map((b: any) => ({
            id: b.id,
            title: b.title
          }))
        )
      } catch (err) {
        toast.error(String(t("addUserBonus.loadDepositBonusesFailed")))
      } finally {
        setIsLoadingDepositBonuses(false)
      }
    }

    if (open) {
      loadDepositBonuses()
    }
  }, [open, user?.tenant_id, savedBonus?.id])

  useEffect(() => {
    if (!open) return

    if (savedBonus?.id && formik.values.bonusId && formik.values.bonusId !== savedBonus.id) {
      setSavedBonus(null)
    }
  }, [open, formik.values.bonusId, savedBonus?.id])

  return (
    <>
      <div className="flex flex-col gap-4">
        {isLoadingDepositBonuses ? (
          <div className="flex justify-center py-4">
            <span className="loading loading-spinner loading-md" />
          </div>
        ) : (
          <BonusOfferSelection
            bonusId={formik.values.bonusId}
            bonuses={depositBonuses}
            onChange={(id) => formik.setFieldValue("bonusId", id)}
            onCreate={() => setOpenBonusModal(true)}
            touched={formik.touched.bonusId}
            error={formik.errors.bonusId}
            savedPromo={savedBonus}
          />
        )}

        <InputField
          name="code"
          title={t("addUserBonus.promoCodeOptional")}
          value={formik.values.code}
          onChange={formik.handleChange}
          altLabelTwo={t("addUserBonus.promoCodeAutoGenerate")}
        />
      </div>
      <ModalBonus data={null} handler={closeBonusModalHandler} open={openBonusModal} saveId={true} />
    </>
  )
})

DepositBonusTab.displayName = "DepositBonusTab"
